import { PostgrestError } from "@supabase/supabase-js";

/**
 * Wraps a Supabase query result and throws if there's an error.
 * Eliminates the need for `if (error) throw error` after every call.
 *
 * Usage:
 *   const data = await query(supabase.from("products").select("*"));
 *   const { data, count } = await queryWithCount(supabase.from("products").select("*", { count: "exact" }));
 */
export async function query<T>(
  promise: PromiseLike<{ data: T; error: PostgrestError | null }>
): Promise<T> {
  const { data, error } = await promise;
  if (error) throw error;
  return data;
}

export async function queryWithCount<T>(
  promise: PromiseLike<{ data: T; error: PostgrestError | null; count: number | null }>
): Promise<{ data: T; count: number | null }> {
  const { data, error, count } = await promise;
  if (error) throw error;
  return { data, count };
}

/**
 * Extracts a user-friendly error message from any error type.
 * Handles Supabase PostgrestError, standard Error, and unknown types.
 */
export function getErrorMessage(error: unknown): string {
  if (error && typeof error === "object") {
    // PostgrestError has .message
    if ("message" in error && typeof (error as any).message === "string") {
      return (error as any).message;
    }
  }
  if (typeof error === "string") return error;
  return "Error desconocido";
}
